import type { QueryClient } from "@-/com.npmjs.tanstack__react-query";
import { hasLength } from "@-/com.npmjs.ts-array-length";

const pendingHashesByQueryClient = new Map<QueryClient, Set<string>>();
const scheduledQueryClients = new Set<QueryClient>();

/**
 * Queue `queryHashes` (TanStack's semantic key hashes — the keys of the
 * registration registry) for invalidation on `queryClient`, coalesced per
 * microtask window: a burst of committed writes (a multi-statement
 * transaction's post-commit flush, or a sync batch applying many rows)
 * collapses into ONE invalidation pass instead of one per matched event.
 * Without this, N writes × M watched keys produced an N×M refetch storm on
 * every reveal/sync.
 *
 * The flush is a SINGLE `invalidateQueries({ predicate })` covering every
 * queued hash, not one `invalidateQueries({ exact: true, queryKey })` per key.
 * The per-key form looks cheaper but is quadratic: each call runs two full
 * cache scans (invalidate, then refetch) and, because `exact` matching goes
 * through `hashQueryKeyByOptions`, re-serializes the filter key with
 * `JSON.stringify` for EVERY entry in the cache. Both factors grow through a
 * session — the registry used to retain every historical key while the
 * cache accumulated — so the post-commit stall grew ~95ms per reviewed card,
 * reaching a full second of frozen JS between COMMIT and the first refetch.
 * A predicate keyed on the precomputed `query.queryHash` is one scan of O(1)
 * lookups with no hashing at all, and is exactly equivalent: nothing in the app
 * configures a `queryKeyHashFn`, so `query.queryHash` IS `hashKey(queryKey)`.
 */
export function queueCoalescedInvalidations(
  queryClient: QueryClient,
  queryHashes: readonly string[],
): void {
  if (hasLength(queryHashes, 0)) {
    return;
  }
  let pendingHashes = pendingHashesByQueryClient.get(queryClient);
  if (pendingHashes === undefined) {
    pendingHashes = new Set();
    pendingHashesByQueryClient.set(queryClient, pendingHashes);
  }
  for (const queryHash of queryHashes) {
    pendingHashes.add(queryHash);
  }
  if (scheduledQueryClients.has(queryClient)) {
    return;
  }
  scheduledQueryClients.add(queryClient);
  queueMicrotask(() => {
    scheduledQueryClients.delete(queryClient);
    const queuedHashes = pendingHashesByQueryClient.get(queryClient);
    pendingHashesByQueryClient.delete(queryClient);
    if (queuedHashes === undefined || queuedHashes.size === 0) {
      return;
    }
    void queryClient.invalidateQueries({
      predicate: (query) => queuedHashes.has(query.queryHash),
    });
  });
}
